/**
 * /earthworks/inventory/locations — where stock is kept (EW-9): yards,
 * stockpiles. Full width; each location opens its own page
 * (/earthworks/inventory/locations/:id, or /new). Written by admin / office.
 *
 * A location still holding stock, or still some item's home, cannot be deleted:
 * the API answers 409 `location_has_stock` / `location_is_home` and the page
 * says the sentence.
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MapPin, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { useAuth } from '../../../contexts/AuthContext';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import {
  useCreateEwMaterialLocation,
  useDeleteEwMaterialLocation,
  useEwMaterialLocations,
  useUpdateEwMaterialLocation,
} from '../../../hooks/useEwInventory';
import { blankToNull, canAdminEw, ewRefusal } from '../../../lib/earthworks';
import type { EwCatalogEntry, EwMaterialLocation, EwMaterialLocationWrite } from '../../../types/earthworks';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useBackToList, useOpenRecord } from '../../downhole/recordPage';
import { EW_EYEBROW, EW_INPUT, EW_PRIMARY, EW_SECONDARY, EwDialog, EwEmpty, EwField, EwFormError, EwLoading, EwPanel, EwRegister, type EwColumn } from '../EwBits';

export default function EwMaterialLocationsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const query = useEwMaterialLocations();
  const rows = query.data?.data ?? [];
  const columns: EwColumn<EwMaterialLocation>[] = [
    { key: 'name', header: 'Location', render: (l) => <span className="font-semibold text-white">{l.name}</span> },
    { key: 'kind', header: 'Kind', render: (l) => l.kindLabel || l.kind },
    { key: 'address', header: 'Address', render: (l) => l.address || '—' },
    { key: 'home', header: 'Home of', align: 'right', render: (l) => `${l.homeItems} item${l.homeItems === 1 ? '' : 's'}` },
  ];
  return (
    <div data-testid="ew-material-locations-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={`${EW_EYEBROW} · Inventory`}
        title="Stock locations"
        description="Yards and stockpiles where inventory is kept."
        onBack={() => navigate('/earthworks/inventory')}
        backLabel="Back"
        stats={[{ icon: <MapPin className="size-4" aria-hidden="true" />, label: 'Locations', tone: 'neutral', loading: query.isLoading, value: displayValue(query.isLoading ? undefined : rows.length) }]}
        actions={
          canAdminEw(user?.role) ? (
            <button type="button" className={DH_BAND_SECONDARY} onClick={() => navigate('/earthworks/inventory/locations/new')}>
              <Plus className="size-4" aria-hidden="true" />
              New location
            </button>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <EwPanel eyebrow="Inventory" title="Locations" testId="ew-location-register">
            <EwRegister
              query={query}
              rows={rows}
              columns={columns}
              rail={() => 'border-l-fi-steel'}
              onOpen={(l) => openRecord(`/earthworks/inventory/locations/${l.id}`)}
              subject="locations"
              testId="ew-location"
              minWidth="min-w-[700px]"
              empty={<EwEmpty>No locations yet. Add a yard or stockpile to keep stock against.</EwEmpty>}
              card={(l) => (
                <>
                  <p className="text-sm font-extrabold text-white">{l.name}</p>
                  <p className="mt-0.5 text-xs text-fieldiq-muted">{l.kindLabel || l.kind} · {l.address || '—'}</p>
                </>
              )}
            />
          </EwPanel>
        </div>
      </section>
    </div>
  );
}

function LocationForm({
  location,
  kinds,
  pending,
  error,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  location: EwMaterialLocation | null;
  kinds: EwCatalogEntry[];
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (body: EwMaterialLocationWrite) => void;
}) {
  const [f, setF] = useState({ name: location?.name ?? '', kind: location?.kind ?? 'yard', address: location?.address ?? '', notes: location?.notes ?? '' });
  const [localError, setLocalError] = useState<string | null>(null);
  return (
    <form
      className="space-y-4 px-4 py-4"
      data-testid="ew-location-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!f.name.trim()) return setLocalError('Enter the location name.');
        setLocalError(null);
        onSubmit({ name: f.name.trim(), kind: f.kind, address: blankToNull(f.address), notes: blankToNull(f.notes) });
      }}
    >
      <EwFormError message={localError ?? error} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <EwField label="Name">
          <input className={EW_INPUT} aria-label="Location name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </EwField>
        <EwField label="Kind">
          <select className={EW_INPUT} aria-label="Location kind" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
            {(kinds.length ? kinds : [{ value: 'yard', label: 'Yard' }]).map((k) => (
              <option key={k.value} value={k.value}>{k.label}</option>
            ))}
          </select>
        </EwField>
        <EwField label="Address" hint="Optional">
          <input className={EW_INPUT} aria-label="Address" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
        </EwField>
      </div>
      <EwField label="Notes" hint="Optional">
        <input className={EW_INPUT} aria-label="Location notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </EwField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={EW_SECONDARY} onClick={onCancel} disabled={pending}>Cancel</button>
        <button type="submit" className={EW_PRIMARY} disabled={pending}>{pending ? 'Saving…' : submitLabel}</button>
      </div>
    </form>
  );
}

/** /earthworks/inventory/locations/:id and /new. */
export function EwMaterialLocationScreen() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const back = useBackToList('/earthworks/inventory/locations');
  const { user } = useAuth();
  const canEdit = canAdminEw(user?.role);
  const query = useEwMaterialLocations();
  const location = isNew ? null : (query.data?.data ?? []).find((l) => l.id === id) ?? null;
  const create = useCreateEwMaterialLocation();
  const update = useUpdateEwMaterialLocation();
  const del = useDeleteEwMaterialLocation();
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  return (
    <div data-testid="ew-location-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={`${EW_EYEBROW} · Stock location`}
        title={isNew ? 'New location' : location?.name || (query.isLoading ? 'Location' : '—')}
        onBack={back}
        backLabel="Back"
        actions={
          !isNew && location && canEdit ? (
            <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
              <Trash2 className="size-4" aria-hidden="true" />
              Delete
            </button>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <EwLoading label="Loading location" />
          ) : !isNew && !location ? (
            <EwPanel eyebrow="Inventory" title="Location">
              <EwEmpty>This location was not found. It may have been deleted.</EwEmpty>
            </EwPanel>
          ) : !canEdit ? (
            <EwPanel eyebrow="Inventory" title={location?.name ?? 'Location'}>
              <p className="px-4 py-4 text-sm text-fieldiq-text">{location ? `${location.kindLabel || location.kind} · ${location.address || 'No address'}` : 'Only the office adds locations.'}</p>
            </EwPanel>
          ) : (
            <EwPanel eyebrow="Inventory" title={isNew ? 'New location' : 'Edit location'}>
              <LocationForm
                location={location}
                kinds={query.data?.kinds ?? []}
                pending={create.isPending || update.isPending}
                error={error}
                submitLabel={isNew ? 'Add location' : 'Save location'}
                onCancel={back}
                onSubmit={(body) => {
                  const done = { onSuccess: () => { toast.success(isNew ? 'Location added' : 'Location saved'); navigate('/earthworks/inventory/locations', { replace: true }); },
                    onError: (err: unknown) => setError(ewRefusal(err, 'The location could not be saved.')) };
                  if (isNew) create.mutate(body, done);
                  else update.mutate({ id: id as string, body }, done);
                }}
              />
            </EwPanel>
          )}
        </div>
      </section>
      {location && canEdit && (
        <EwDialog
          open={deleting}
          title="Delete location"
          onClose={() => setDeleting(false)}
          footer={
            <>
              <button type="button" className={EW_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>Keep location</button>
              <button
                type="button"
                className={EW_PRIMARY}
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(location.id, {
                    onSuccess: () => { toast.success('Location deleted'); navigate('/earthworks/inventory/locations', { replace: true }); },
                    onError: (err) => setDeleteError(ewRefusal(err, 'The location could not be deleted.')),
                  })
                }
              >
                {del.isPending ? 'Deleting…' : 'Delete location'}
              </button>
            </>
          }
        >
          <EwFormError message={deleteError} />
          <p className="text-sm text-fieldiq-text">Removes {location.name}. A location still holding stock, or still an item&apos;s home, cannot be deleted.</p>
        </EwDialog>
      )}
    </div>
  );
}
